import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Stack,
} from '@mui/material';
import ExtensionRoundedIcon from '@mui/icons-material/ExtensionRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { useTranslation, Trans } from 'react-i18next';
import { isExtensionInstalled } from './instatExtension';

const DISMISS_KEY = 'instat.extension.banner.dismissedUntil';
const DISMISS_DAYS = 7;

function resolveDownloadUrl() {
  if (process.env.REACT_APP_INSTAT_EXTENSION_URL) {
    return process.env.REACT_APP_INSTAT_EXTENSION_URL;
  }
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const userId = user?.id || '';
  return `${base}/api/instat/extension.zip?userId=${userId}`;
}

function canInstallExtension() {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  return user?.can_install_instat_extension === true;
}

export default function InstatExtensionBanner() {
  const { t } = useTranslation();
  const [installed, setInstalled] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!canInstallExtension()) {
      setDismissed(true);
      return;
    }
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (ts && Date.now() < ts) setDismissed(true);
    setInstalled(isExtensionInstalled());
  }, []);

  const onDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 86400_000));
    setDismissed(true);
  };

  const onDownloadAndShowInstructions = () => {
    window.location.href = resolveDownloadUrl();
    setDialogOpen(true);
  };

  if (installed || dismissed) return null;

  return (
    <>
      <Alert
        severity="info"
        icon={<ExtensionRoundedIcon fontSize="small" />}
        action={
          <Stack direction="row" spacing={1}>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              startIcon={<DownloadRoundedIcon />}
              onClick={onDownloadAndShowInstructions}
            >
              {t('instat.extensionBanner.download')}
            </Button>
            <Button color="inherit" size="small" onClick={onDismiss}>
              {t('instat.extensionBanner.later')}
            </Button>
          </Stack>
        }
        sx={{ mb: 2, borderRadius: 2 }}
      >
        <Trans i18nKey="instat.extensionBanner.message">
          Install the <strong>Valencia Hudl Helper</strong> extension to connect to Hudl
          automatically when you open a video.
        </Trans>
      </Alert>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('instat.extensionBanner.dialogTitle')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <Trans i18nKey="instat.extensionBanner.dialogIntro">
              Download of <code>valencia-hudl-helper.zip</code> has started.
            </Trans>
          </Typography>

          <Box component="ol" sx={{ pl: 2.5, '& li': { mb: 1.3 }, fontSize: 14 }}>
            <li>
              <Trans i18nKey="instat.extensionBanner.step1">
                <strong>Unzip</strong> the file into a folder (e.g. <code>~/Documents/hudl-helper</code>).
              </Trans>
            </li>
            <li>
              <Trans i18nKey="instat.extensionBanner.step2">
                Open <code>chrome://extensions</code> in Chrome.
              </Trans>
            </li>
            <li>
              <Trans i18nKey="instat.extensionBanner.step3">
                Enable <strong>Developer mode</strong> (toggle in the top right).
              </Trans>
            </li>
            <li>
              <Trans i18nKey="instat.extensionBanner.step4">
                Click <strong>Load unpacked</strong> and pick the unzipped folder.
              </Trans>
            </li>
            <li>
              <Trans i18nKey="instat.extensionBanner.step5">
                <strong>Reload</strong> this Valencia tab (Ctrl/Cmd+R). The banner disappears once the extension is detected.
              </Trans>
            </li>
          </Box>

          <Button
            variant="text"
            size="small"
            startIcon={<DownloadRoundedIcon />}
            href={resolveDownloadUrl()}
            sx={{ mt: 1.5 }}
          >
            {t('instat.extensionBanner.redownload')}
          </Button>

          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
            {t('instat.extensionBanner.privacyNote')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {t('instat.extensionBanner.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
